import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';
import { demoProducts } from '../data/demoData';

/**
 * ============================================
 * useProducts Hook
 * ============================================
 * Custom hook for fetching and managing products
 */
export const useProducts = ({ search = '', category = '', page = 1, limit = 10, sortBy = 'createdAt', sortOrder = 'desc' } = {}) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        total: 0
    });

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const queryParams = new URLSearchParams({
                page: page || 1,
                limit: limit || 12,
                ...(category && { category: category }),
                ...(search && { search: search }),
                sortBy: sortBy || 'createdAt',
                sortOrder: sortOrder || 'desc'
            });

            const response = await fetch(`${API_BASE_URL}/products?${queryParams}`);
            const data = await response.json();

            if (data.success) {
                setProducts(data.data);
                setPagination({
                    page: data.page,
                    pages: data.pages,
                    total: data.total
                });
            } else {
                throw new Error(data.message);
            }
        } catch (err) {
            console.warn('API unavailable, using demo data:', err.message);
            setError(err.message);
            
            // Filter demo data based on params
            let filtered = [...demoProducts];
            
            if (category) {
                filtered = filtered.filter(p => p.category === category);
            }
            
            if (search) {
                const searchLower = search.toLowerCase();
                filtered = filtered.filter(p => 
                    p.name.toLowerCase().includes(searchLower) ||
                    p.description.toLowerCase().includes(searchLower)
                );
            }
            
            setProducts(filtered);
            setPagination({
                page: 1,
                pages: 1,
                total: filtered.length
            });
        } finally {
            setLoading(false);
        }
    }, [search, category, page, limit, sortBy, sortOrder]);

    // Fetch products when parameters change
    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    return { products, loading, error, pagination, fetchProducts };
};

/**
 * ============================================
 * useCategories Hook
 * ============================================
 * Custom hook for fetching categories
 */
export const useCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/products/categories`);
                const data = await response.json();
                
                if (data.success) {
                    setCategories(data.data);
                }
            } catch (err) {
                console.warn('Using fallback categories');
                setCategories([
                    { name: 'Voilage', count: 2 },
                    { name: 'Occultant', count: 2 },
                    { name: 'Lin', count: 2 },
                    { name: 'Velours', count: 1 },
                    { name: 'Semi-Occultant', count: 1 },
                    { name: 'Thermique', count: 1 },
                    { name: 'Décoratif', count: 1 }
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    return { categories, loading };
};

/**
 * ============================================
 * useDebounce Hook
 * ============================================
 * Debounces a value by specified delay
 */
export const useDebounce = (value, delay = 300) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(timer);
    }, [value, delay]);

    return debouncedValue;
};

export default useProducts;
